/*
 * Copyright (c) Nexign, JSC, 2024.
 */
/**
 *
 *
 * @author Viacheslav Smalenski
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.29.0
 */

import { Typography } from '@nexign/scc-ui-kit';

import { ProgressBar, ToolTip } from '@/main/components/common';
import { IPageSubscriberIdTranslation } from '@/main/modules/internationalization/types/pages/subscriberId';
import { floor10 } from '@/main/utils/decimalsFormat';
import { replaceDecimalDot } from '@/main/utils/replaceDecimalDot';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { ProgressBarWrapper } from '@pages/PageSubscriberId/components/Info/components/styled/SubscriberIdInfoStyled';

import { IInfoTariffDiscount } from '../../types';
import { TInfoTariffDiscountsOptionalValues } from '../SubscriberIdInfoTariffDiscounts';

const OVERFLOW_DIGITS = 5;

const formatToRounded = (valueToRound: number) => {
    const stringifiedValue = valueToRound.toString();
    const isValueWithDecimals = stringifiedValue.includes('.');
    let roundedValue: number | undefined = valueToRound;

    if (stringifiedValue.length > OVERFLOW_DIGITS + 1 && isValueWithDecimals) {
        roundedValue = floor10(valueToRound, -2);
    } else if (stringifiedValue.length > OVERFLOW_DIGITS + 2 && isValueWithDecimals) {
        roundedValue = floor10(valueToRound, 0);
    }
    return replaceDecimalDot(roundedValue);
};

const isLabelRightOverflow = (value: number | string, valueTotal: number | string) => {
    const stringifiedValue = value.toString();
    const stringifiedValueTotal = valueTotal.toString();

    if (stringifiedValue.includes('.')) {
        return stringifiedValue.length > OVERFLOW_DIGITS + 1 || stringifiedValueTotal.length > OVERFLOW_DIGITS;
    }
    if (stringifiedValueTotal.includes('.')) {
        return stringifiedValue.length > OVERFLOW_DIGITS || stringifiedValueTotal.length > OVERFLOW_DIGITS + 1;
    }

    return stringifiedValue.length > OVERFLOW_DIGITS || stringifiedValueTotal.length > OVERFLOW_DIGITS;
};

const formatLabelRightValue = (value?: number | string) => {
    if (value === undefined) return undefined;

    const stringifiedValue = value.toString();

    if (stringifiedValue.includes('.')) {
        return (
            stringifiedValue.substring(0, OVERFLOW_DIGITS + 1) +
            (stringifiedValue.length > OVERFLOW_DIGITS + 1 ? '...' : '')
        );
    }

    return stringifiedValue.substring(0, OVERFLOW_DIGITS) + (stringifiedValue.length > OVERFLOW_DIGITS ? '...' : '');
};

const labelRight = (
    discount: TInfoTariffDiscountsOptionalValues,
    cutValue = true,
    translation: IPageSubscriberIdTranslation['tabs']['info']['tariffDiscounts'],
) => {
    if (discount.isUnlimitedVolume) {
        return translation.unlimited;
    }

    return stringFormatParams(
        discount.unit === discount.unitTotal ? translation.rangeWithUnit : translation.rangeWithTwoUnit,
        {
            unit: discount.unit,
            unitTotal: discount.unitTotal,
            value: cutValue ? formatLabelRightValue(discount.value) : discount.value,
            valueTotal: cutValue ? formatLabelRightValue(discount.valueTotal) : discount.valueTotal,
        },
    );
};

export const mapDiscountToComponent = (
    discount: IInfoTariffDiscount,
    index: number,
    translation: IPageSubscriberIdTranslation['tabs']['info']['tariffDiscounts'],
) => {
    const roundedDiscountValue = formatToRounded(discount.value);

    const roundedDiscountValueTotal = formatToRounded(discount.valueTotal);
    const formattedDiscount = {
        ...discount,
        value: roundedDiscountValue,
        valueTotal: roundedDiscountValueTotal,
    };

    let valuePercentage: number;

    if (discount.isUnlimitedVolume) {
        valuePercentage = 100;
    } else if (discount.valuePercentage < 1 && discount.valuePercentage > 0) {
        // Минимальное отображение остатков в ProgressBar - 1%
        valuePercentage = 1;
    } else {
        valuePercentage = discount.valuePercentage;
    }

    return (
        <ProgressBarWrapper key={index}>
            <ProgressBar
                isColorChangedOnValue
                availableVolumeLider={discount.availableVolumeLider as number}
                baseUnitsLider={discount.baseUnitsLider}
                baseVolumeLider={discount.baseVolumeLider}
                className="subscriber-id-info__range"
                isShared={discount.isShared}
                isSpend={discount.isSpend}
                labelLeft={discount.label}
                labelRight={
                    <ToolTip
                        disableHoverListener={
                            !!roundedDiscountValue &&
                            !!roundedDiscountValueTotal &&
                            !isLabelRightOverflow(roundedDiscountValue, roundedDiscountValueTotal)
                        }
                        placement="top"
                        title={labelRight(formattedDiscount, false, translation)}
                    >
                        <div>{labelRight(formattedDiscount, true, translation) ?? ''}</div>
                    </ToolTip>
                }
                unitOfMeasure={discount.unitTotal}
                value={discount.valueWithUnits as number || discount.value}
                valuePercentage={valuePercentage}
                // Минимальное отображение остатков - 1%
                valueTotal={discount.valueTotal}
            />
            {discount.dateTo && (
                <Typography className="subscriber-id-info__date-to" variant="body2">
                    {stringFormatParams(translation.dateToText, { dateTo: discount.dateTo })}
                </Typography>
            )}
        </ProgressBarWrapper>
    );
};
